// src/services/espacioServiciosService.ts
import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

const API_URL = 'espacio-servicios/';

export interface EspacioServicio {
    id: number;
    espacio: number;
    servicio: number;
    fecha_creacion: string;
    estado: boolean;
    espacio_nombre?: string;
    servicio_nombre?: string;
}

export interface DisponibilidadEspacio {
    espacio_id: number;
    espacio_nombre: string;
    disponible: boolean;
    ocupado: boolean;
    motivo: string;
    servicio_nombre: string;
    detalle_citas: any[];
    hora_inicio_solicitada: string;
    hora_fin_solicitada: string;
    fecha: string;
}

export interface EspacioDisponible {
    id: number;
    espacio_nombre: string;
    servicio_nombre: string;
    hora_inicio_disponible: string;
    hora_fin_disponible: string;
}

export interface HorarioDisponible {
    inicio: string;
    fin: string;
    duracion_minutos: number;
    duracion_horas: number;
}

export interface OcupacionMensualDetalle {
    salida_estimada?: boolean;
    tipo: 'cita' | 'consumo_horas_regalo';
    id: number;
    codigo?: string | null;
    cliente?: string;
    hora_inicio: string;
    hora_fin: string;
    uso_horas_regalo?: number;
    estado?: string;
}

export interface OcupacionMensualDia {
    fecha: string;
    ocupado: true;
    detalles?: OcupacionMensualDetalle[];
}

export interface OcupacionMensual {
    espacio_id: number;
    espacio_nombre: string;
    mes: string;
    dias_ocupados: OcupacionMensualDia[];
}

export interface OcupacionEspacioHoy {
    salida_estimada?: boolean;
    tipo: 'cita' | 'consumo_horas_regalo' | 'ocupacion';
    id?: number;
    codigo?: string | null;
    cliente?: string;
    hora_inicio?: string;
    hora_fin?: string | null;
    estado?: string;
    en_curso?: boolean;
}

export interface EspacioOcupadoHoy {
    espacio_id: number;
    espacio_nombre: string;
    servicio_nombre: string;
    sucursal: string | null;
    ocupaciones: OcupacionEspacioHoy[];
    total_ocupaciones: number;
    disponible: false;
}

export interface OcupacionDiariaEspacios {
    fecha: string;
    total_espacios_ocupados: number;
    espacios_ocupados: EspacioOcupadoHoy[];
}

// Funciones existentes...
export const fetchEspacioServicios = async (servicioId?: number, sucursalId?: number): Promise<EspacioServicio[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<EspacioServicio[]>(API_URL, {
            params: {
                ...(servicioId ? { servicio: servicioId } : {}),
                ...(sucursalId ? { sucursal_id: sucursalId } : {}),
            },
        });
        return response.data;
    });
};

export const createEspacioServicio = async (data: {
    espacio: number;
    servicio: number;
    estado?: boolean;
}) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(API_URL, data);
        return response.data;
    });
};

export const updateEspacioServicio = async (
    id: number,
    data: {
        espacio: number;
        servicio: number;
        estado?: boolean;
    }
) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.put(`${API_URL}${id}/`, data);
        return response.data;
    });
};

export const deleteEspacioServicio = async (id: number) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.delete(`${API_URL}${id}/`);
        return response.data;
    });
};

// Verificar disponibilidad de múltiples espacios (AHORA CON HORARIO)
export const verificarDisponibilidadEspacios = async (
    espacioIds: number[],
    fecha: string,
    horaInicio: string,
    horaFin: string
): Promise<DisponibilidadEspacio[]> => {
    return handleRequestWithAuth(async () => {
        const params = new URLSearchParams();
        espacioIds.forEach(id => params.append('espacio_ids[]', String(id)));
        params.set('fecha', fecha);
        params.set('hora_inicio', horaInicio);
        params.set('hora_fin', horaFin);
        
        const response = await axiosInstance.get(`${API_URL}disponibilidad/`, { params });
        return response.data;
    });
};

// Obtener espacios disponibles por servicio (AHORA CON HORARIO)
export const fetchEspaciosDisponiblesPorServicio = async (
    servicioId: number,
    fecha: string,
    horaInicio: string,
    horaFin: string,
    fechaFin?: string,
    diasHabiles = false,
    temporizador = false
): Promise<EspacioDisponible[]> => {
    return handleRequestWithAuth(async () => {
        const params: any = { 
            servicio_id: servicioId,
            fecha: fecha,
            fecha_fin: fechaFin || fecha,
            hora_inicio: horaInicio,
            hora_fin: horaFin,
            dias_habiles: diasHabiles,
            ...(temporizador ? { temporizador: true } : {})
        };
        
        const response = await axiosInstance.get(`${API_URL}disponibles_por_servicio/`, { params });
        return response.data;
    });
};

// Obtener horarios disponibles para un espacio
export const fetchHorariosDisponibles = async (
    espacioId: number,
    fecha?: string
): Promise<{
    espacio_id: number;
    espacio_nombre: string;
    fecha: string;
    horario_operacion: { inicio: string; fin: string };
    horarios_disponibles: HorarioDisponible[];
    total_horarios_disponibles: number;
    bloques_ocupados: any[];
}> => {
    return handleRequestWithAuth(async () => {
        const params: any = { espacio_id: espacioId };
        if (fecha) params.fecha = fecha;
        
        const response = await axiosInstance.get(`${API_URL}horarios_disponibles/`, { params });
        return response.data;
    });
};

export const fetchOcupacionMensual = async (
    espacioId: number,
    mes: string
): Promise<OcupacionMensual> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}ocupacion-mensual/`, {
            params: { espacio_id: espacioId, mes },
        });
        return response.data;
    });
};

export const fetchEspaciosOcupadosPorFecha = async (
    fecha: string,
    sucursalId: number
): Promise<OcupacionDiariaEspacios> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}ocupados_por_fecha/`, {
            params: { fecha, sucursal_id: sucursalId },
        });
        return response.data;
    });
};

// Función para obtener la fecha y hora actual formateada
export const obtenerFechaHoraActual = () => {
    const ahora = new Date();
    
    return {
        fecha: ahora.toISOString().split('T')[0], // YYYY-MM-DD
        hora: ahora.toTimeString().split(' ')[0], // HH:MM:SS
        horaSimple: ahora.toTimeString().split(':').slice(0, 2).join(':'), // HH:MM
        horaInicio: ahora.toTimeString().split(' ')[0], // HH:MM:SS
        horaFin: new Date(ahora.getTime() + 60 * 60 * 1000).toTimeString().split(' ')[0] // +1 hora
    };
};

// Calcular hora fin basada en minutos disponibles
export const calcularHoraFin = (horaInicio: string, minutosDisponibles: number): string => {
    const [horas, minutos, segundos] = horaInicio.split(':').map(Number);
    const fechaHora = new Date();
    fechaHora.setHours(horas, minutos, segundos || 0, 0);
    fechaHora.setMinutes(fechaHora.getMinutes() + minutosDisponibles);
    
    return fechaHora.toTimeString().split(' ')[0];
};

// Verificar disponibilidad en tiempo real antes de iniciar consumo
export const verificarDisponibilidadTiempoReal = async (
    espacioId: number,
    fecha: string,
    horaInicio: string,
    horaFin: string
): Promise<{ disponible: boolean; motivo?: string; detalle_citas?: any[] }> => {
    return handleRequestWithAuth(async () => {
        const params: any = {
            espacio_ids: JSON.stringify([espacioId]),
            fecha: fecha,
            hora_inicio: horaInicio,
            hora_fin: horaFin
        };
        
        const response = await axiosInstance.post(`${API_URL}verificar_disponibilidad_tiempo_real/`, params);
        const resultados = response.data;
        
        if (resultados.length > 0) {
            return {
                disponible: resultados[0].disponible,
                motivo: resultados[0].motivo,
                detalle_citas: resultados[0].detalle_citas || []
            };
        }
        
        return { disponible: false, motivo: "Error en la verificación" };
    });
};

